import { safeLocalStorageGet, safeLocalStorageSetDelete } from '~/utils/localStorage';

interface UserData {
  name: string;
  email: string;
  phoneNumber: string;
}

interface EncryptedData {
  encryptedName: string;
  encryptedEmail: string;
  encryptedPhoneNumber: string;
}

const base64ToBytes = (base64: string) => {
  const binString = window.atob(base64);
  return Uint8Array.from(binString, (m) => m.codePointAt(0) || 0);
};

const bytesToBase64 = (bytes: Uint8Array) => {
  const binString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join('');
  return window.btoa(binString);
};

// Main class to handle form data encryption and storage
class SecureFormHandler {
  private readonly APIKEY: string = '';
  private readonly STORAGE_KEY: string = 'vuukle_quiz_user_form_data';

  constructor(apiKey: string) {
    this.APIKEY = apiKey;
  }

  // XOR encryption function
  private encrypt(plaintext: string, key: string): string {
    try {
      let result = '';
      for (let i = 0; i < plaintext.length; i++) {
        const charCode = plaintext.charCodeAt(i) ^ key.charCodeAt(i % key.length);
        result += String.fromCharCode(charCode);
      }
      return bytesToBase64(new TextEncoder().encode(result));
    } catch (error) {
      throw new Error('Encryption failed: ' + error.message);
    }
  }

  // XOR decryption function
  private decrypt(encryptedText: string, key: string): string {
    try {
      const decoded = new TextDecoder().decode(base64ToBytes(encryptedText));
      let result = '';
      for (let i = 0; i < decoded.length; i++) {
        const charCode = decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length);
        result += String.fromCharCode(charCode);
      }
      return result;
    } catch (error) {
      throw new Error('Decryption failed: ' + error.message);
    }
  }

  // Store encrypted data in local storage
  private storeData(encryptedData: EncryptedData): void {
    try {
      import(/* webpackChunkName: "cmg" */ '../../../ConsentManager').then((consentManager) => {
        consentManager.default.executeWhenStorageValueCanBeSet(this.STORAGE_KEY, () => {
          safeLocalStorageSetDelete(this.STORAGE_KEY, JSON.stringify(encryptedData));
        });
      });
    } catch (error) {
      throw new Error('Storage operation failed: ' + error.message);
    }
  }

  // Retrieve encrypted data from local storage
  private retrieveData(): EncryptedData | null {
    try {
      const data = safeLocalStorageGet(this.STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      throw new Error('Retrieval operation failed: ' + error.message);
    }
  }

  // Delete user's data
  public deleteData(): void {
    safeLocalStorageSetDelete(this.STORAGE_KEY, null);
  }

  // Main function to handle form data
  public processFormData(userData: UserData): void {
    try {
      // Input validation
      if (!userData.name || !userData.email || !userData.phoneNumber) {
        throw new Error('All fields are required');
      }

      // Encrypt each field
      const encryptedData: EncryptedData = {
        encryptedName: this.encrypt(userData.name, this.APIKEY),
        encryptedEmail: this.encrypt(userData.email, this.APIKEY),
        encryptedPhoneNumber: this.encrypt(userData.phoneNumber, this.APIKEY),
      };

      // Store encrypted data
      this.storeData(encryptedData);
    } catch (error) {
      throw new Error('Form processing failed: ' + error.message);
    }
  }

  // Retrieve and decrypt user data
  public retrieveUserData(): UserData | null {
    try {
      const encryptedData = this.retrieveData();
      if (!encryptedData) return null;

      return {
        name: this.decrypt(encryptedData.encryptedName, this.APIKEY),
        email: this.decrypt(encryptedData.encryptedEmail, this.APIKEY),
        phoneNumber: this.decrypt(encryptedData.encryptedPhoneNumber, this.APIKEY),
      };
    } catch (error) {
      throw new Error('Data retrieval failed: ' + error.message);
    }
  }
}

export default SecureFormHandler;
